import { MarkHeading } from '../../../../components/MarkControls';
import * as layout from '../../../../components/markLayout.module.css';
import P from '@hero/ui-kit/typography/P';
import { MarkButton, MarkChoiceLabel, MarkChoice } from '../../../../components/MarkControls';
import React, { useId } from 'react';
import RefPicker from '../RefPicker';
import ExitReasonSelect from '../ExitReasonSelect';
import { SEND_WINDOW_TIME_ZONE_HINT } from '../../../../constants';
import type { GraphError } from '../../../../hooks/useDripGraphValidation';
import type { ActivityStep, DripStep, AuthorableDripExitReason } from '../../../../types';
type Props = {
    step: ActivityStep;
    allSteps: DripStep[];
    onChange: (patch: Partial<ActivityStep>) => void;
    onEditTemplate?: () => void;
    onSaveAndEditTemplate?: () => void;
    saveAndEditTemplateDisabled?: boolean;
    sendWindowErrors?: GraphError[];
    channelLocked?: boolean;
    channelLockedReason?: string;
    canEditTemplate: boolean;
    isPersisted: boolean;
    unsavedChannelChange?: boolean;
};
const ActivityForm: React.FC<Props> = ({
    step,
    allSteps,
    onChange,
    onEditTemplate,
    onSaveAndEditTemplate,
    saveAndEditTemplateDisabled,
    sendWindowErrors = [],
    channelLocked,
    channelLockedReason,
    canEditTemplate,
    isPersisted,
    unsavedChannelChange
}) => {
    const radioId = useId();
    const showExitReason = step.next === 'exit';
    return (
        <div className={layout.form}>
            <fieldset
                aria-label="Activity type"
                disabled={channelLocked}
                style={{
                    margin: 0
                }}
                className={layout.fieldset}
            >
                <MarkHeading role="h6">Activity type</MarkHeading>
                {channelLocked && channelLockedReason && (
                    <P
                        styles={{
                            color: 'var(--mark-muted)',
                            margin: '0 0 0.4rem'
                        }}
                    >
                        {channelLockedReason}
                    </P>
                )}
                <div className={layout.choiceGroup}>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name={`${radioId}-activity`}
                            value="send_email"
                            checked={step.activity === 'send_email'}
                            onChange={() => onChange({ activity: 'send_email' })}
                        />
                        Send email
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name={`${radioId}-activity`}
                            value="send_sms"
                            checked={step.activity === 'send_sms'}
                            onChange={() => onChange({ activity: 'send_sms' })}
                        />
                        Send SMS
                    </MarkChoiceLabel>
                </div>
            </fieldset>
            <RefPicker
                label="Next"
                value={step.next}
                onChange={(v) => onChange({ next: v })}
                steps={allSteps}
                currentStepId={step.id}
            />
            {showExitReason && (
                <ExitReasonSelect
                    value={step.exit_reason as AuthorableDripExitReason | undefined}
                    onChange={(v) => onChange({ exit_reason: v })}
                />
            )}
            {!isPersisted && canEditTemplate && onSaveAndEditTemplate ? (
                <div className={layout.field}>
                    <MarkButton
                        variant="primary"
                        type="button"
                        onClick={onSaveAndEditTemplate}
                        disabled={saveAndEditTemplateDisabled}
                        title={
                            saveAndEditTemplateDisabled
                                ? 'Fix validation errors before saving.'
                                : unsavedChannelChange
                                  ? 'Saves the channel change first, then opens the template editor for the new channel.'
                                  : 'Saves the step graph first, then opens the template editor.'
                        }
                        style={{
                            alignSelf: 'flex-start'
                        }}
                    >
                        Save &amp; edit template
                    </MarkButton>
                    {saveAndEditTemplateDisabled && sendWindowErrors.length > 0 && (
                        <>
                            <div role="alert" aria-live="polite" className={layout.notice}>
                                {sendWindowErrors.map((error) => (
                                    <P
                                        key={error.message}
                                        noDefaultMargin
                                        size="small"
                                        styles={{
                                            color: 'var(--mark-danger)'
                                        }}
                                    >
                                        {error.message}
                                    </P>
                                ))}
                            </div>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                {SEND_WINDOW_TIME_ZONE_HINT}
                            </P>
                        </>
                    )}
                </div>
            ) : (
                <MarkButton
                    type="button"
                    onClick={onEditTemplate}
                    disabled={!canEditTemplate || !onEditTemplate || !isPersisted}
                    title={
                        !onEditTemplate
                            ? 'Template editing is not yet available.'
                            : !isPersisted
                              ? unsavedChannelChange
                                  ? 'Save the channel change first.'
                                  : 'Save the drip first so the step exists on the server.'
                              : !canEditTemplate
                                ? 'Save the drip first to enable template editing.'
                                : undefined
                    }
                    style={{
                        alignSelf: 'flex-start'
                    }}
                >
                    Edit template
                </MarkButton>
            )}
        </div>
    );
};
export default ActivityForm;
